import type {
	CoffeeType,
	PlanCropStage,
	PlanIrrigationSystem,
	PlanNutrient,
	PlantingForm,
	RangeMode,
	ReferenceBiennial,
	ReferenceRegion,
	ReferenceSeason,
	SoilClassification,
	SoilGroupKey,
	SoilTexture,
	StateSectionKey,
	PlanSectionKey,
} from '../types/parameters.types';
import { STATE_NAMES } from './stateNames';

export const ALL_STATE_CODES = Object.keys(STATE_NAMES).sort();

export const PLAN_NUTRIENTS: PlanNutrient[] = ['N', 'P', 'K', 'Ca', 'Mg', 'S', 'B', 'Cu', 'Fe', 'Mn', 'Zn'];

export const COFFEE_LABELS: Record<CoffeeType, string> = {
	cafeArabica: 'Café arábica',
	cafeConilon: 'Café conilon',
};

export const COFFEE_OPTIONS = (Object.keys(COFFEE_LABELS) as CoffeeType[]).map((value) => ({
	value,
	label: COFFEE_LABELS[value],
}));

export const CROP_STAGE_LABELS: Record<PlanCropStage, string> = {
	growth: 'Crescimento',
	production: 'Produção',
	prune: 'Recepa',
};

export const REGION_LABELS: Record<ReferenceRegion, string> = {
	norte: 'Norte',
	nordeste: 'Nordeste',
	'centro-Oeste': 'Centro-Oeste',
	sudeste: 'Sudeste',
	sul: 'Sul',
	zona: 'Zona da Mata',
	triangulo: 'Triângulo',
	geral: 'Geral',
	leste: 'Leste',
};

export const BIENNIAL_LABELS: Record<ReferenceBiennial, string> = {
	alta: 'Alta',
	media: 'Média',
	baixa: 'Baixa',
};

export const SEASON_LABELS: Record<ReferenceSeason, string> = {
	outono: 'Outono',
	inverno: 'Inverno',
	primavera: 'Primavera',
	verao: 'Verão',
};

export const TEXTURE_LABELS: Record<SoilTexture, string> = {
	arenosa: 'Arenosa',
	media: 'Média',
	argilosa: 'Argilosa',
	muitoArgilosa: 'Muito argilosa',
};

export const PLANTING_FORM_LABELS: Record<PlantingForm, string> = {
	cova: 'Cova',
	sulco: 'Sulco',
};

export const IRRIGATION_LABELS: Record<PlanIrrigationSystem | 'broad', string> = {
	none: 'Sem irrigação',
	drip: 'Gotejamento',
	sprinkler: 'Aspersão',
	microSprinkler: 'Microaspersão',
	pivot: 'Pivô',
	broad: 'A lanço',
};

export const RANGE_MODE_LABELS: Record<RangeMode, string> = {
	general: 'Faixas gerais',
	region: 'Região e bienalidade',
	season: 'Estação do ano',
};

export const CLASSIFICATION_ORDER: SoilClassification[] = ['Muito_Baixo', 'Baixo', 'Medio', 'Alto', 'Muito_Alto'];

export const CLASSIFICATION_LABELS: Record<SoilClassification, string> = {
	Muito_Baixo: 'Muito baixo',
	Baixo: 'Baixo',
	Medio: 'Médio',
	Alto: 'Alto',
	Muito_Alto: 'Muito alto',
};

export const PARAM_LABELS: Record<string, string> = {
	N: 'Nitrogênio (N)',
	P: 'Fósforo (P)',
	Pm1: 'Fósforo (P)',
	Pm1PlantioCafe: 'Fósforo no plantio',
	K: 'Potássio (K)',
	Ca: 'Cálcio (Ca)',
	Mg: 'Magnésio (Mg)',
	S: 'Enxofre (S)',
	B: 'Boro (B)',
	Cu: 'Cobre (Cu)',
	Fe: 'Ferro (Fe)',
	Mn: 'Manganês (Mn)',
	Zn: 'Zinco (Zn)',
	MO: 'Matéria orgânica',
	Al: 'Alumínio (Al)',
	satAl: 'Saturação de alumínio',
	satSB: 'Saturação de bases',
	satCa: 'Saturação de cálcio',
	satMg: 'Saturação de magnésio',
	satK: 'Saturação de potássio',
	satNa: 'Saturação de sódio',
	pHagua: 'pH em H₂O',
	pHCaCl2: 'pH em CaCl₂',
	Ca_Mg: 'Relação Ca/Mg',
	CaMg: 'Relação Ca/Mg',
};

export const SOIL_GROUP_CODES: Record<SoilGroupKey, string[]> = {
	macro: ['Pm1', 'Pm1PlantioCafe', 'K', 'Ca', 'Mg', 'S'],
	micro: ['B', 'Cu', 'Fe', 'Mn', 'Zn'],
	indexes: ['MO', 'Al', 'satK', 'satCa', 'satMg', 'satSB', 'satNa', 'satAl', 'Ca_Mg', 'pHagua', 'pHCaCl2'],
};

export const LIMING_CODES = ['satSB', 'CaMg'];

export const PLASTERING_CODES = ['Al', 'Ca', 'satCa'];

export const PERCENT_CODES = ['satSB', 'satCa'];

export const STATE_SECTION_LABELS: Record<StateSectionKey, string> = {
	macro: 'Macronutrientes',
	micro: 'Micronutrientes',
	indexes: 'Índices químicos',
	liming: 'Calagem',
	plastering: 'Gessagem',
	p2o5: 'P₂O₅ no plantio',
	foliar: 'Foliar',
	dris: 'DRIS',
};

export const PLAN_SECTION_LABELS: Record<PlanSectionKey, string> = {
	root: 'Fator radicular',
	extraction: 'Extração',
	exports: 'Exportação',
	efficiency: 'Eficiências',
	minimum: 'Demanda mínima',
	nutrients: 'Nutrientes',
};

export const SOIL_KIND_LABELS = {
	fixed: 'Fixa',
	Prem: 'Dependente de P-rem',
	clay: 'Dependente de argila / textura',
} as const;

export const TABLE_PAGE_SIZE = 8;
